import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "../utils";
import {
  ArrowRight,
  Monitor,
  Smartphone,
  Globe,
  Shield,
  Zap,
  Play,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import Hero3D from "../components/Hero3D";
import ServiceCard from "../components/ServiceCard";
import WhyChooseUs from "../components/WhyChooseUs";
import UnmatchedServices from "../components/UnmatchedServices";

const STATS = [
  { number: "500+", label: "Klien Puas" },
  { number: "99%", label: "Uptime" },
  { number: "24/7", label: "Support" },
  { number: "5★", label: "Rating" },
];

const SERVICES = [
  {
    icon: Monitor,
    title: "ARJUNA",
    description: "Website responsif dan aplikasi web modern dengan performa tinggi",
  },
  {
    icon: Smartphone,
    title: "SRIKANDI",
    description: "Aplikasi mobile native dan cross-platform yang user-friendly",
  },
  {
    icon: Globe,
    title: "PRAJURIT",
    description: "Migrasi dan optimasi infrastruktur cloud untuk skalabilitas",
  },
];

export default function Home() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-gray-50/50 via-white to-gray-100/30 pt-24 pb-16">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,#e5e7eb_1px,transparent_0)] [background-size:20px_20px] opacity-20" />

        <div className="container mx-auto px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div
              className={`transition-all duration-1000 ${isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-8"
                }`}
            >
              <Badge
                variant="secondary"
                className="mb-6 px-4 py-2 bg-gray-100 text-gray-700 border-gray-200"
              >
                <Zap className="w-4 h-4 mr-2" />
                Solusi IT Terdepan 2024
              </Badge>

              <h1 className="text-5xl lg:text-7xl font-bold mb-8 leading-tight">
                <span className="bg-gradient-to-r from-gray-700 to-gray-900 bg-clip-text text-transparent">
                  Transformasi Digital
                </span>
                <br />
                <span className="text-gray-900">untuk Bisnis Modern</span>
              </h1>

              <p className="text-xl lg:text-2xl text-gray-600 mb-10 leading-relaxed">
                Ghandiva menghadirkan solusi teknologi inovatif yang
                mengoptimalkan operasional bisnis Anda dengan pendekatan yang
                personal dan hasil yang terukur.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-start items-start mb-16">
                <Link to={createPageUrl("Services")}>
                  <Button
                    size="lg"
                    className="px-8 py-4 bg-gray-700 hover:bg-gray-800 text-white group transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-gray-500/25 flex items-center"
                  >
                    <span>Jelajahi Layanan</span>
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>

                <Link to={createPageUrl("Gallery")}>
                  <Button
                    variant="outline"
                    size="lg"
                    className="px-8 py-4 border-gray-300 text-gray-700 hover:bg-gray-50 group flex items-center"
                  >
                    <Play className="mr-2 w-5 h-5 group-hover:scale-110 transition-transform" />
                    Lihat Demo
                  </Button>
                </Link>

              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {STATS.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-2xl font-bold text-gray-700 mb-1">
                      {stat.number}
                    </div>
                    <div className="text-sm text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* 3D Hero Element */}
            <div
              className={`relative transition-all duration-1000 delay-300 ${isVisible
                ? "opacity-100 translate-x-0"
                : "opacity-0 translate-x-8"
                }`}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-gray-600/20 to-gray-400/20 rounded-full blur-3xl scale-150"></div>
                <Hero3D />
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 w-16 h-16 bg-white rounded-2xl shadow-lg border border-gray-200 flex items-center justify-center animate-bounce">
                <Shield className="w-8 h-8 text-gray-700" />
              </div>
              <div className="absolute -bottom-6 -left-6 w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl shadow-lg flex items-center justify-center animate-pulse">
                <Globe className="w-10 h-10 text-gray-700" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <WhyChooseUs />

      {/* Services Preview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Layanan Unggulan <span className="text-gray-700">Ghandiva</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Solusi komprehensif untuk mendorong pertumbuhan bisnis Anda di era
              digital.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {SERVICES.map((service, index) => (
              <ServiceCard key={index} {...service} index={index} />
            ))}
          </div>

          <div className="flex justify-center mt-12">
            <Link to={createPageUrl("Services")}>
              <Button size="lg" variant="outline" className="group">
                Lihat Semua Layanan
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <UnmatchedServices />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-gray-700 to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 bg-white rounded-full blur-2xl animate-pulse"></div>
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-white rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="container mx-auto px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl font-bold text-white mb-6">
            Siap Mentransformasi Bisnis Anda?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Konsultasi gratis untuk mengetahui solusi IT terbaik untuk kebutuhan
            bisnis Anda bersama Ghandiva
          </p>
          <div className="flex justify-center mt-8">
            <Link to={createPageUrl("Contact")}>
              <Button
                size="lg"
                className="px-8 py-4 bg-white text-gray-700 hover:bg-gray-100 group shadow-lg flex items-center"
              >
                Hubungi Ghandiva Sekarang
                <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
